import type { FC, ReactNode } from 'react'
import { Divider, Typography } from '@tunasong/ui-lib'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import styles from './theory-card.module.css'

export interface TheoryCardProps {
  /** Lexicon entry id — drives the heading and the level-resolved body copy. */
  lexiconId: string
  /**
   * Optional action slot (the "Hear it" play button in S1, the loop button
   * in S2, etc.). Lives below the body, divider above. Caller owns the icon
   * and the click handler.
   */
  action?: ReactNode
  /**
   * Optional descriptive suffix appended to the heading. Used by S1 to add
   * `(V → I)` to "Authentic cadence" at intermediate / advanced levels. The
   * suffix is not pulled from the lexicon — it's a per-surface fact (the
   * cadence kind) the caller computes.
   */
  headingSuffix?: string
}

/**
 * Reusable card shell for every Phase-1 theory surface (S1 cadence, S2
 * progression, S3a modal, S4 suggestion explainer). Joni's UX spec §2.1 owns
 * the layout. Consumers:
 *   - pass a `lexiconId` and the card pulls displayName + level-resolved body
 *     via `useTheoryCopy`,
 *   - render `null` when `useTheoryCopy` returns `null` (theoryLevel off /
 *     unknown id),
 *   - optionally provide an `action` slot for "Hear it" affordances.
 *
 * Accessibility:
 *   - `role="dialog"` + `aria-labelledby` wires the heading to the dialog.
 *   - The play button (in the action slot) is keyboard-reachable by virtue
 *     of being a real `<button>` inside the dialog.
 *   - Escape dismissal is owned by the parent `Popup` component (the card
 *     doesn't manage its own open/close — it's a content-only shell).
 */
export const TheoryCard: FC<TheoryCardProps> = props => {
  const { lexiconId, action, headingSuffix } = props
  const copy = useTheoryCopy(lexiconId)
  if (!copy) {
    return null
  }
  const headingId = `theory-card-heading-${lexiconId}`
  const heading = headingSuffix ? `${copy.displayName} (${headingSuffix})` : copy.displayName
  return (
    <div className={styles.card} role="dialog" aria-labelledby={headingId}>
      <Typography id={headingId} variant="subtitle1" className={styles.heading}>
        <TheoryGlyphs>{heading}</TheoryGlyphs>
      </Typography>
      <Typography variant="body2" className={styles.body}>
        <TheoryGlyphs>{copy.body}</TheoryGlyphs>
      </Typography>
      {action ? (
        <>
          <Divider />
          <div className={styles.actions}>{action}</div>
        </>
      ) : null}
    </div>
  )
}
